/**
 * Fallback file service for browsers without File System Access API
 * Uses traditional file input/output methods
 */
export class FallbackFileService {
  // In-memory file storage for the session
  private static files = new Map<string, {
    name: string
    content: string
    lastModified: Date
  }>()

  /**
   * Open file using input element
   */
  static async openFile(): Promise<{ name: string; content: string; handle?: any } | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.md,.txt'
      input.multiple = false
      
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0]
        if (!file) {
          resolve(null)
          return
        }
        
        try {
          // Use FileReader for better compatibility with Safari
          const reader = new FileReader()
          
          reader.onload = (event) => {
            const content = event.target?.result as string
            
            // Store in memory for later save
            this.files.set(file.name, {
              name: file.name,
              content,
              lastModified: new Date()
            })
            
            resolve({
              name: file.name,
              content,
              handle: { 
                name: file.name, 
                kind: 'file',
                isFallback: true 
              }
            })
          }
          
          reader.onerror = (error) => {
            console.error('Failed to read file:', error)
            resolve(null)
          }
          
          // Read as text
          reader.readAsText(file)
        } catch (err) {
          console.error('Failed to read file:', err)
          resolve(null)
        }
      }
      
      input.click()
    })
  }

  /**
   * Open multiple files
   */
  static async openFiles(): Promise<Array<{ name: string; content: string }> | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.md,.txt'
      input.multiple = true
      
      input.onchange = async (e) => {
        const files = Array.from((e.target as HTMLInputElement).files || [])
        if (files.length === 0) {
          resolve(null)
          return
        }
        
        try {
          const results = await Promise.all(
            files.map(async (file) => {
              const content = await file.text()
              
              // Store in memory
              this.files.set(file.name, {
                name: file.name,
                content,
                lastModified: new Date()
              })
              
              return { name: file.name, content }
            })
          )
          
          resolve(results)
        } catch (err) {
          console.error('Failed to read files:', err)
          resolve(null)
        }
      }
      
      input.click()
    })
  }

  /**
   * Save file by downloading it
   */
  static saveFile(filename: string, content: string): boolean {
    try {
      // Update in-memory storage
      this.files.set(filename, {
        name: filename,
        content,
        lastModified: new Date()
      })
      
      // Create blob and download
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      URL.revokeObjectURL(url)
      
      return true
    } catch (err) {
      console.error('Failed to save file:', err)
      return false
    }
  }

  /**
   * Save file with dialog (just downloads with suggested name)
   */
  static saveAsNewFile(content: string, suggestedName: string = 'untitled.md'): boolean {
    return this.saveFile(suggestedName, content)
  }

  /**
   * Get list of files in memory (for current session)
   */
  static getFileList(): Array<{ name: string; lastModified: Date }> {
    return Array.from(this.files.entries()).map(([name, file]) => ({
      name,
      lastModified: file.lastModified
    }))
  }

  /**
   * Get file from memory if available
   */
  static getFile(filename: string): { name: string; content: string } | null {
    const file = this.files.get(filename)
    if (!file) return null
    
    return {
      name: file.name,
      content: file.content
    }
  }

  /**
   * Clear all files from memory
   */
  static clearFiles(): void {
    this.files.clear()
  }

  /**
   * Check if file exists in memory
   */
  static hasFile(filename: string): boolean {
    return this.files.has(filename)
  }

  /**
   * Delete file from memory
   */
  static deleteFile(filename: string): boolean {
    return this.files.delete(filename)
  }

  /**
   * Export all files as a zip (requires additional library)
   * For now, this creates a simple text file with all content
   */
  static exportAll(): void {
    if (this.files.size === 0) {
      alert('No files to export')
      return
    }

    let exportContent = '=== Exported Files ===\n\n'
    
    this.files.forEach((file, name) => {
      exportContent += `\n=== File: ${name} ===\n`
      exportContent += `Last Modified: ${file.lastModified.toISOString()}\n\n`
      exportContent += file.content
      exportContent += '\n\n' + '='.repeat(50) + '\n'
    })
    
    this.saveFile('exported_files.txt', exportContent)
  }
}